import { getNumbersFromStr } from '@alfa-bank/site-helpers-form/text/get-numbers-from-str';

import { isEditableIndex } from '../helpers';

import { COUNTRY_CODE, INVALID_CODES, NUMBER_SYMBOL } from './config';
import type {
    ICursorArgs,
    TClosestEditableF,
    TStartsInvalidF,
    TInputRef,
    TInputSelection,
    TMaskOptions,
} from './models';

/**
 * Определяет, нужно ли убрать из пользовательского ввода префикс маски,
 * когда пользователь произвел вставку значения из буфера обмена
 */
const shouldRemovePrefixFromInput: TStartsInvalidF = (value, fieldLength) => {
    const valueStr = value.join('');
    const isLongerThanField = value.length > fieldLength;
    const isInvalidStart = INVALID_CODES.some(reg => reg.test(valueStr));

    return isLongerThanField && isInvalidStart;
};

/**
 * Определяет совпадает ли начало пользовательского ввода с префиксом маски,
 * когда пользователь произвел вставку значения из буфера обмена
 */
export const validateInput = (valueNumbersArray: string[], maskNumbersArray: string[]) => {
    const maskWithoutCountryCode = maskNumbersArray.slice(1);
    const shouldBeValidated = maskWithoutCountryCode.length === valueNumbersArray.length;

    if (!shouldBeValidated) {
        return true;
    }

    return maskWithoutCountryCode.every(
        (digit, index) => digit === NUMBER_SYMBOL || digit === valueNumbersArray[index]
    );
};

/**
 * Определяется является ли символ в маске числом
 * (либо нередактируемым числом в самой маске, либо символом обозначающим пользовательский ввод)
 */
const isMaskNumber = (char: string) => /\d/.test(char) || char === NUMBER_SYMBOL;

/**
 * Генерирует префикс из маски
 * (возвращает маску до первого редактируемого символа)
 */
export const generatePrefix = (mask: string, numberSymbol: string) => {
    const firstEditableIndex = Array.from(mask).findIndex(char => char === numberSymbol);

    return mask.slice(0, firstEditableIndex);
};

/**
 * Генерирует плейсхолдер из маски
 * (заменяет все редактируемые символы заданным символом)
 */
export const generatePlaceholder = (
    mask: string,
    numberSymbol: string,
    placeholderNumberSymbol = '0'
) => Array.from(mask, char => (char === numberSymbol ? placeholderNumberSymbol : char)).join('');

/**
 * Находит индекс ближайшего редактируемого символа, находящегося слева от курсора
 */
export const closestEditableIndex: TClosestEditableF = ({ input, maskOptions }) => {
    const { maskFull, prefix } = maskOptions;
    const prefixIndex = prefix.length;
    const currIndex = Number(input?.selectionEnd) - 1;

    if (currIndex < prefixIndex) return undefined;

    let index = currIndex;

    /* istanbul ignore next */
    while (index > prefixIndex && maskFull.permanents.includes(index)) {
        index -= 1;
    }

    return index;
};

/**
 * Находит индекс ближайшего нередактируемого числа, находящегося справа от заданной позиции
 */
export const nextNonEditableNumberIndex = (currrentIndex: number, maskOptions: TMaskOptions) =>
    Math.min(
        ...maskOptions.maskFull.permanents.filter(
            maskIndex =>
                maskIndex - currrentIndex >= 0 &&
                isMaskNumber(maskOptions.maskFull.asArray[maskIndex]) &&
                maskOptions.maskFull.asArray[maskIndex] !== NUMBER_SYMBOL
        )
    );

const addPermSymbol = (acc: number[], character: string, index: number) => {
    const isPermanentSymbol = character !== NUMBER_SYMBOL;

    return isPermanentSymbol ? [...acc, index] : acc;
};

/**
 * Создает массив индексов редактируемых символов без учета специальных символов
 */
const getMaskEditableNumbers = (mask: string, maskFullPermanents: number[]) => {
    let maskNumberIndex = -1;

    return Array.from(mask, (chr, index) => {
        if (!isMaskNumber(chr)) {
            return null;
        }

        maskNumberIndex += 1;
        if (isEditableIndex(index, maskFullPermanents)) {
            return maskNumberIndex;
        }

        return null;
    }).filter(maskEditableNumberIndex => maskEditableNumberIndex !== null) as number[];
};

export const getMaskOptions = (mask: string) => {
    // массив всех символов в маске
    const maskFullArray = mask.split('');

    // массив индексов нередактируемых символов
    const maskFullPermanents = maskFullArray.reduce(addPermSymbol, [] as number[]);

    // маска без специальных символов, только числа
    const maskNumbersArray = maskFullArray.filter(isMaskNumber);

    // индексы редактируемых чисел в maskNumbers
    const maskNumbersEditables = getMaskEditableNumbers(mask, maskFullPermanents);

    // длина последовательности чисел на окончании маски
    const maskTailLength = maskNumbersArray.length - (Math.max(...maskNumbersEditables) + 1);

    return {
        // полная маска
        maskFull: {
            asArray: maskFullArray,
            asString: mask,
            permanents: maskFullPermanents,
        },
        // маска без спецсимволов, только числа
        maskNumbers: {
            asArray: maskNumbersArray,
            asString: maskNumbersArray.join(''),
            editables: maskNumbersEditables,
            tailLength: maskTailLength,
        },
        lastEditablePosition: mask.length,
        prefix: generatePrefix(mask, NUMBER_SYMBOL),
    } as const;
};

/**
 * Очищает маску после удаленного символа,
 * предотвращая попадание чисел из маски на позиции, где должен быть пользовательский ввод
 */
export const getMaskRemovalFunc =
    (maskOptions: TMaskOptions) => (newValue: string, oldValue: string) => {
        const maskOffset = Math.min(newValue.length - oldValue.length, 0);
        if (!maskOffset) {
            return newValue;
        }

        const deletionPoint = Array.from(oldValue).findIndex(
            (char, index) => char !== newValue[index]
        );

        const nextFixedNumberIndex =
            nextNonEditableNumberIndex(deletionPoint + 1, maskOptions) + maskOffset;

        return newValue.slice(0, nextFixedNumberIndex);
    };

const getValueNumbers = (maskOptions: TMaskOptions, value: string) => {
    const { maskNumbers, prefix } = maskOptions;
    const valueNumbers: string = getNumbersFromStr(value);
    const valueNumbersArray = Array.from(valueNumbers);

    if (
        shouldRemovePrefixFromInput(
            valueNumbersArray,
            maskNumbers.asString.length - maskNumbers.tailLength
        )
    ) {
        valueNumbersArray.splice(0, getNumbersFromStr(prefix).length);

        const hasCountryCode =
            valueNumbersArray.length === maskNumbers.asString.length &&
            INVALID_CODES.some(reg => reg.test(valueNumbersArray.join('')));

        if (!validateInput(valueNumbersArray.slice(hasCountryCode ? 1 : 0), maskNumbers.asArray)) {
            valueNumbersArray.splice(0);
        }

        if (valueNumbersArray.length && !hasCountryCode) {
            valueNumbersArray.unshift(COUNTRY_CODE);
        }
    }

    return valueNumbersArray;
};

export const getFormatFunc = (maskOptions: TMaskOptions) => (value: string) => {
    const { maskFull, maskNumbers, prefix } = maskOptions;
    const valueNumbersArray = getValueNumbers(maskOptions, value);

    const valueEditableNumbersArrray = valueNumbersArray.filter((_, index) =>
        maskNumbers.editables.includes(index)
    );
    // Дописана ли маска после последнего введенного числа
    let injectedTrailingMask = false;

    return maskFull.asArray.reduce((result, char, index) => {
        const isEditableSymbol = isEditableIndex(index, maskFull.permanents);
        const injectedEditableNumbers =
            valueEditableNumbersArrray.length === 0 && index > prefix.length - 1;

        if (injectedEditableNumbers && injectedTrailingMask) {
            return result;
        }

        if (injectedEditableNumbers && isEditableSymbol) {
            injectedTrailingMask = true;

            return result;
        }

        const injectingSymbol = isEditableSymbol
            ? valueEditableNumbersArrray.shift() || char
            : char;

        return `${result}${injectingSymbol}`;
    }, '');
};

export const getInitialCursor = (prefixLength: number): TInputSelection => ({
    start: prefixLength,
    end: prefixLength,
});

export const getCursorFromRef = (inputRef: TInputRef): TInputSelection => ({
    start: inputRef.current?.selectionStart,
    end: inputRef.current?.selectionEnd,
});

export const getCursorFromValue = (value: string): TInputSelection => ({
    start: value.length,
    end: value.length,
});

export const getNextNumPosition = (options: ICursorArgs) => {
    const {
        maskOptions: { maskFull },
        position,
    } = options;

    const maskAfterCursor = position ? maskFull.asArray.slice(position - 1) : maskFull.asArray;

    /**
     * Поскольку после введенного пользователем числа также дописывается маска, курсор должен быть
     * помещен после неё
     */
    let foundNextDigit = false;
    const nextPosition = maskAfterCursor.findIndex((item, index, arr) => {
        if (item === NUMBER_SYMBOL) {
            foundNextDigit = true;
        }

        const nextSymbolIsNotPartOfMask =
            arr[index + 1] === undefined || arr[index + 1] === NUMBER_SYMBOL;

        return foundNextDigit && nextSymbolIsNotPartOfMask;
    });

    const offset = nextPosition >= 0 ? nextPosition : maskAfterCursor.length;

    return position ? position + offset : offset;
};
